import axiosInstance from '../../../config/axios';

/**
 * DocumentService - API Client for Employee Documents
 *
 * Document-specific endpoints (no employee ID in path):
 *
 * GET    /api/admin/documents/:did/                    - Get document details (includes versions, current_version)
 * GET    /api/admin/documents/:did/events/             - Get document event timeline
 * GET    /api/admin/documents/:did/download[?version]  - Download PDF (current or specific version)
 * GET    /api/admin/documents/:did/preview[?version]   - Server-side preview (current or specific version)
 *
 * Employee-specific endpoints (require employee ID):
 *
 * GET    /api/admin/employees/:eid/documents/                    - List all documents for employee
 * POST   /api/admin/employees/:eid/documents/                    - Create new document (via ContractWizard)
 * POST   /api/admin/employees/:eid/documents/:did/regenerate/    - Regenerate document (creates version +1)
 * POST   /api/admin/employees/:eid/documents/:did/upload-init/   - Initialize signed PDF upload (S3 presigned)
 * POST   /api/admin/employees/:eid/documents/:did/upload-complete/ - Complete signed PDF upload
 * POST   /api/admin/employees/:eid/documents/:did/send-esign/    - Send document for eSign
 * GET    /api/admin/employees/:eid/documents/:did/esign-status/  - Get eSign status
 * DELETE /api/admin/employees/:eid/documents/:did/               - Delete document
 *
 * Legacy Note: uploadSigned() uses old single-stage upload, prefer uploadSignedInit/Complete for S3
 */

export interface BundleItem {
  kind: 'COVER' | 'MAIN' | 'ANNEX';
  order: number;
  document_id: number;
  title: string;
  pdf_url?: string | null;
}

export interface BundleMerge {
  success: boolean;
  warnings: string[];
}

export interface Bundle {
  id: number;
  attachments_count: number;
  items?: BundleItem[];
  combined_pdf_url?: string | null;
  suggested_file_name?: string;
  merge?: BundleMerge;
}

export interface DocumentInstance {
  id: number;
  employee: number;
  template_id: string;
  template_name?: string;
  template_key?: string;
  position_title?: string;  // ← NEU: Position für die der Vertrag erstellt wurde
  version: number;
  status: 'DRAFT' | 'READY' | 'SENT' | 'SIGNED' | 'EXPIRED' | 'ERROR';
  contains_compensation: boolean;
  created_at: string;
  updated_at: string;
  pdf_url?: string;
  signed_pdf_url?: string;
  esign_envelope_id?: string;
  esign_status?: 'pending' | 'sent' | 'delivered' | 'completed' | 'declined' | 'voided';
  esign_sent_at?: string;
  esign_signed_at?: string;
  error_message?: string;
  error_trace_id?: string;
  current_version?: DocumentVersion;
  versions?: DocumentVersion[];
  bundle_id?: number;
  bundle?: Bundle;
  bundle_summary?: Bundle;
}

export interface DocumentListResponse {
  results: DocumentInstance[];
  count: number;
}

export interface ESignSendPayload {
  recipient_email?: string;
  recipient_name?: string;
  message?: string;
}

export interface ESignSendResponse {
  envelope_id: string;
  status: string;
  sent_at: string;
}

export async function uploadDocument(
  employeeId: number,
  file: File,
  title: string,
  containsCompensation: boolean = false
): Promise<DocumentInstance> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title);
  formData.append('contains_compensation', String(containsCompensation));
  const { data } = await axiosInstance.post<DocumentInstance>(
    `/api/admin/employees/${employeeId}/documents/upload/`,
    formData
  );
  return data;
}

export async function fetchDocuments(employeeId: number): Promise<DocumentInstance[]> {
  const { data } = await axiosInstance.get<DocumentListResponse | DocumentInstance[]>(
    `/api/admin/employees/${employeeId}/documents/`
  );
  if (Array.isArray(data)) {
    return data;
  }
  return data.results || [];
}

export async function getDocumentDetail(documentId: number): Promise<DocumentInstance> {
  const { data } = await axiosInstance.get<DocumentInstance>(
    `/api/admin/documents/${documentId}/`
  );
  return data;
}

export async function downloadDocument(documentId: number, version?: number): Promise<Blob> {
  const params = version ? `?version=${version}` : '';
  const { data } = await axiosInstance.get(
    `/api/admin/documents/${documentId}/download${params}`,
    {
      responseType: 'blob',
    }
  );
  return data;
}

export async function previewDocument(documentId: number, version?: number): Promise<Blob> {
  const params = version ? `?version=${version}` : '';
  const { data } = await axiosInstance.get(
    `/api/admin/documents/${documentId}/preview${params}`,
    {
      responseType: 'blob',
    }
  );
  return data;
}

export async function downloadPdfBytes(documentId: number, version?: number): Promise<ArrayBuffer> {
  const params = version ? `?version=${version}` : '';
  const { data } = await axiosInstance.get(
    `/api/admin/documents/${documentId}/download${params}`,
    {
      responseType: 'arraybuffer',
    }
  );
  return data;
}

async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error: any) {
      const isServerError = error?.response?.status >= 500;
      const shouldRetry = isServerError && attempt < maxRetries;

      if (!shouldRetry) {
        throw error;
      }

      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, Math.min(delay, 8000)));
    }
  }

  throw new Error('Failed after all retries');
}

export async function getPreviewPdfBlob(documentId: number, version?: number): Promise<Blob> {
  const params = version ? `?version=${version}` : '';

  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.get(
      `/api/admin/documents/${documentId}/preview${params}`,
      {
        responseType: 'blob',
      }
    );
    return data;
  });
}

export async function downloadPdfBlob(documentId: number, version?: number): Promise<Blob> {
  const params = version ? `?version=${version}` : '';

  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.get(
      `/api/admin/documents/${documentId}/download${params}`,
      {
        responseType: 'blob',
      }
    );
    return data;
  });
}

export async function sendForESign(
  employeeId: number,
  documentId: number,
  payload: ESignSendPayload
): Promise<ESignSendResponse> {
  const { data } = await axiosInstance.post<ESignSendResponse>(
    `/api/admin/employees/${employeeId}/documents/${documentId}/send-esign/`,
    payload
  );
  return data;
}

export async function getESignStatus(
  employeeId: number,
  documentId: number
): Promise<{
  envelope_id: string;
  status: string;
  sent_at?: string;
  signed_at?: string;
  view_url?: string;
}> {
  const { data } = await axiosInstance.get(
    `/api/admin/employees/${employeeId}/documents/${documentId}/esign-status/`
  );
  return data;
}

export async function deleteDocument(employeeId: number, documentId: number): Promise<void> {
  await axiosInstance.delete(`/api/admin/employees/${employeeId}/documents/${documentId}/`);
}

export interface DocumentEvent {
  id: number;
  event_type: 'created' | 'regenerated' | 'sent' | 'viewed' | 'signed' | 'uploaded' | 'downloaded' | 'signed_uploaded' | 'error';
  timestamp: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  details?: Record<string, any>;
  metadata?: {
    version?: number;
    previous_version?: number;
    error_message?: string;
    trace_id?: string;
  };
}

export interface DocumentVersion {
  id: number;
  version: number;
  rendered_at: string;
  pdf_size_bytes?: number;
  pdf_hash?: string;
  is_current: boolean;
  file_url?: string;
  preview_url?: string;
  download_url?: string;
}

export interface RegeneratePayload {
  overrides: Record<string, any>;
  reason?: string;
}

export interface UploadSignedPayload {
  file: File;
  signed_at?: string;
}

export interface UploadInitResponse {
  upload_url: string;
  final_url_hint: string;
}

export interface UploadCompletePayload {
  file_url: string;
}

export async function regenerateDocument(
  employeeId: number,
  documentId: number,
  overrides: Record<string, any>,
  reason?: string
): Promise<DocumentInstance> {
  const { data } = await axiosInstance.post<DocumentInstance>(
    `/api/admin/employees/${employeeId}/documents/${documentId}/regenerate/`,
    { overrides, reason }
  );
  return data;
}

export async function uploadSigned(
  employeeId: number,
  documentId: number,
  file: File,
  signedAt?: string
): Promise<DocumentInstance> {
  const formData = new FormData();
  formData.append('file', file);
  if (signedAt) {
    formData.append('signed_at', signedAt);
  }

  const { data } = await axiosInstance.post<DocumentInstance>(
    `/api/admin/employees/${employeeId}/documents/${documentId}/upload-signed/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data;
}

export async function uploadSignedInit(
  employeeId: number,
  documentId: number
): Promise<UploadInitResponse> {
  const { data } = await axiosInstance.post<UploadInitResponse>(
    `/api/admin/employees/${employeeId}/documents/${documentId}/upload-init/`
  );
  return data;
}

export async function uploadSignedComplete(
  employeeId: number,
  documentId: number,
  fileUrl: string
): Promise<DocumentInstance> {
  const { data } = await axiosInstance.post<DocumentInstance>(
    `/api/admin/employees/${employeeId}/documents/${documentId}/upload-complete/`,
    { file_url: fileUrl }
  );
  return data;
}

export async function resolveDocumentPdfUrl(doc: DocumentInstance): Promise<Blob> {

  if (doc.pdf_url) {
    try {
      const { data } = await axiosInstance.get(doc.pdf_url, { responseType: 'blob' });
      return data;
    } catch (err: any) {
    }
  }

  try {
    const { data } = await axiosInstance.get(
      `/api/admin/documents/${doc.id}/download`,
      { responseType: 'blob' }
    );
    return data;
  } catch (err: any) {
  }

  const { data } = await axiosInstance.get(
    `/api/admin/documents/${doc.id}/preview`,
    { responseType: 'blob' }
  );
  return data;
}

export async function fetchDocumentEvents(documentId: number): Promise<DocumentEvent[]> {
  const { data } = await axiosInstance.get<DocumentEvent[]>(
    `/api/admin/documents/${documentId}/events/`
  );
  return data;
}

export interface CreateAnnexDocumentPayload {
  template_id: string;
  variables: Record<string, any>;
  contains_compensation?: boolean;
}

export async function createAnnexDocument(
  employeeId: number,
  payload: CreateAnnexDocumentPayload
): Promise<DocumentInstance> {
  const { data } = await axiosInstance.post<DocumentInstance>(
    `/api/admin/employees/${employeeId}/documents/`,
    {
      template_id: payload.template_id,
      variables: payload.variables,
      contains_compensation: payload.contains_compensation || false,
    }
  );
  return data;
}
